import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  Button,
  Descriptions,
  Divider,
  Dropdown,
  Image,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getPhotoUrl } from "utils/common";
import RejectModal from "./components/RejectModal";
import * as restaurantIdentityActions from "./slice";

const { Paragraph } = Typography;

function RestaurantIdentity() {
  const dispatch = useDispatch();
  const {
    restaurantIdentities,
    pagination,
    isLoading,
    isProcessing,
    isCompleted,
  } = useSelector((state) => state.restaurantIdentity);

  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectingIdentity, setSelectingIdentity] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterRestaurant, setFilterRestaurant] = useState("");

  useEffect(() => {
    dispatch(restaurantIdentityActions.fetchRestaurantIdentities(pagination));
  }, []);

  useEffect(() => {
    if (isCompleted) {
      setOpenRejectModal(false);
      setSelectingIdentity(null);
    }
  }, [isCompleted]);

  const onVerify = (identity) => {
    Modal.confirm({
      title: "Xác nhận xác minh căn cước công dân",
      content: `Bạn có chắc chắn muốn xác minh căn cước công dân này?`,
      onOk: () => {
        dispatch(
          restaurantIdentityActions.verifyRestaurantIdentity(identity._id)
        );
      },
      centered: true,
    });
  };

  const onReject = (identity) => {
    setSelectingIdentity(identity);
    setOpenRejectModal(true);
  };

  const onDelete = (identity) => {
    Modal.confirm({
      title: "Xác nhận xóa căn cước công dân",
      content: `Bạn có chắc chắn muốn xóa căn cước công dân này?`,
      onOk: () => {
        dispatch(
          restaurantIdentityActions.deleteRestaurantIdentity(identity._id)
        );
      },
      centered: true,
    });
  };

  const onCloseRejectModal = () => {
    setOpenRejectModal(false);
    setSelectingIdentity(null);
  };

  const onViewDetail = (identity) => {
    setSelectingIdentity(identity);
    setOpenDetailModal(true);
  };

  const onCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectingIdentity(null);
  };

  const onSubmitReject = (reason) => {
    if (!reason || !reason.trim()) {
      toast.error("Vui lòng nhập lý do từ chối");
      return;
    }
    if (selectingIdentity?._id) {
      dispatch(
        restaurantIdentityActions.rejectRestaurantIdentity(
          selectingIdentity._id,
          reason
        )
      );
    }
    setOpenRejectModal(false);
  };

  const onChangeSearch = (e) => {
    setSearch(e.target.value);
  };

  const buildParams = (paginationOverride = {}) => {
    const params = {
      ...pagination,
      ...paginationOverride,
    };
    if (
      filterStatus &&
      filterStatus !== null &&
      filterStatus !== undefined &&
      filterStatus !== ""
    ) {
      params.status = filterStatus;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (filterRestaurant && filterRestaurant.trim()) {
      params.restaurant = filterRestaurant.trim();
    }
    return params;
  };

  const onChangeStatusFilter = (value) => {
    const newStatus = value === undefined || value === "" ? null : value;
    setFilterStatus(newStatus);
    const params = {
      ...pagination,
      current: 1,
    };
    if (
      newStatus &&
      newStatus !== null &&
      newStatus !== undefined &&
      newStatus !== ""
    ) {
      params.status = newStatus;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (filterRestaurant && filterRestaurant.trim()) {
      params.restaurant = filterRestaurant.trim();
    }
    dispatch(restaurantIdentityActions.fetchRestaurantIdentities(params));
  };

  const onChangeRestaurantFilter = (e) => {
    setFilterRestaurant(e.target.value);
  };

  const onSearch = () => {
    dispatch(
      restaurantIdentityActions.fetchRestaurantIdentities(
        buildParams({ current: 1 })
      )
    );
  };

  const onTableChange = async (pagination) => {
    dispatch(
      restaurantIdentityActions.fetchRestaurantIdentities(
        buildParams(pagination)
      )
    );
  };

  const onClearFilters = () => {
    setSearch("");
    setFilterStatus(null);
    setFilterRestaurant("");
    dispatch(
      restaurantIdentityActions.fetchRestaurantIdentities({
        ...pagination,
        current: 1,
      })
    );
  };

  const getStatusTag = (status) => {
    switch (status) {
      case "verified":
        return <Tag color="green">Đã xác minh</Tag>;
      case "rejected":
        return <Tag color="red">Đã từ chối</Tag>;
      case "pending":
      default:
        return <Tag color="orange">Chờ xử lý</Tag>;
    }
  };

  const columns = [
    {
      title: "STT",
      align: "center",
      width: "5%",
      render: (text, record, index) => (
        <Typography.Paragraph
          copyable={{
            text: record._id,
            tooltips: ["Sao chép ID", "Đã sao chép!"],
          }}
        >
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </Typography.Paragraph>
      ),
    },
    {
      title: "Quán ăn",
      dataIndex: "restaurant",
      width: "15%",
      render: (restaurant) => (
        <Paragraph
          copyable={{
            text: restaurant?._id,
            tooltips: ["Sao chép ID", "Đã sao chép!"],
          }}
          style={{ margin: 0 }}
        >
          {restaurant?.name || "N/A"}
        </Paragraph>
      ),
    },
    {
      title: "Số CCCD",
      dataIndex: "number",
      width: "12%",
    },
    {
      title: "Họ và tên",
      dataIndex: "name",
      width: "15%",
    },
    {
      title: "Ảnh mặt trước",
      dataIndex: "frontPhoto",
      width: "10%",
      align: "center",
      render: (photo) =>
        photo ? (
          <Image
            alt="Front photo"
            src={getPhotoUrl(photo?.path || null)}
            width={80}
            height={50}
            style={{ objectFit: "cover" }}
          />
        ) : (
          "N/A"
        ),
    },
    {
      title: "Ảnh mặt sau",
      dataIndex: "backPhoto",
      width: "10%",
      align: "center",
      render: (photo) =>
        photo ? (
          <Image
            alt="Back photo"
            src={getPhotoUrl(photo?.path || null)}
            width={80}
            height={50}
            style={{ objectFit: "cover" }}
          />
        ) : (
          "N/A"
        ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: "15%",
      align: "center",
      render: (status, record) => (
        <div style={{ textAlign: "center" }}>
          <div>{getStatusTag(status)}</div>
          {status === "rejected" && record.rejectReason && (
            <div style={{ marginTop: 8, textAlign: "left", maxWidth: 200 }}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                Lý do từ chối:
              </div>
              <Paragraph
                ellipsis={{ rows: 2, expandable: true }}
                style={{ margin: 0, fontSize: 12 }}
              >
                {record.rejectReason}
              </Paragraph>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Ngày xử lý",
      dataIndex: "processedAt",
      width: "12%",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "N/A"),
    },
    {
      title: "Thao tác",
      align: "center",
      width: "10%",
      fixed: "right",
      render: (record) => {
        const KEY_ACTIONS = {
          VIEW_DETAIL: "view_detail",
          VERIFY: "verify",
          REJECT: "reject",
          DELETE: "delete",
        };

        const onClick = ({ key }) => {
          switch (key) {
            case KEY_ACTIONS.VIEW_DETAIL:
              onViewDetail(record);
              break;
            case KEY_ACTIONS.VERIFY:
              onVerify(record);
              break;
            case KEY_ACTIONS.REJECT:
              onReject(record);
              break;
            case KEY_ACTIONS.DELETE:
              onDelete(record);
              break;
            default:
              break;
          }
        };

        const menuItems = [
          {
            key: KEY_ACTIONS.VIEW_DETAIL,
            label: "Xem chi tiết",
            icon: <EyeOutlined />,
          },
          ...(record.status === "pending"
            ? [
                {
                  key: KEY_ACTIONS.VERIFY,
                  label: "Xác minh",
                  icon: <CheckOutlined />,
                },
                {
                  key: KEY_ACTIONS.REJECT,
                  label: "Từ chối",
                  icon: <CloseOutlined />,
                  danger: true,
                },
              ]
            : []),
          {
            key: KEY_ACTIONS.DELETE,
            label: "Xóa",
            icon: <DeleteOutlined />,
            danger: true,
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems, onClick }}
            disabled={isProcessing}
          >
            <Button disabled={isProcessing}>
              <Space>
                Thao tác
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle">
        <Typography.Title level={2}>
          [{pagination?.total || "0"}] Quản lý căn cước công dân
        </Typography.Title>
        <Space size="middle" wrap>
          <Select
            placeholder="Lọc theo trạng thái"
            value={filterStatus || ""}
            onChange={onChangeStatusFilter}
            allowClear
            style={{ width: 180 }}
          >
            <Select.Option value="">Tất cả</Select.Option>
            <Select.Option value="pending">Chờ xử lý</Select.Option>
            <Select.Option value="verified">Đã xác minh</Select.Option>
            <Select.Option value="rejected">Đã từ chối</Select.Option>
          </Select>
          <Input
            placeholder="Tìm theo ID quán"
            value={filterRestaurant}
            onChange={onChangeRestaurantFilter}
            style={{ width: 200 }}
            allowClear
          />
          <Input.Search
            placeholder="Tìm theo tên hoặc số CCCD"
            value={search}
            onChange={onChangeSearch}
            onSearch={onSearch}
            enterButton
            allowClear
            style={{ width: 250 }}
          />
        </Space>
      </Row>
      <Divider />
      <Table
        bordered
        loading={isLoading}
        columns={columns}
        dataSource={restaurantIdentities}
        rowKey={(record) => record._id}
        pagination={pagination}
        onChange={onTableChange}
        scroll={{ x: 1500 }}
      />
      {openRejectModal && (
        <RejectModal
          open={openRejectModal}
          isProcessing={isProcessing}
          onSubmit={onSubmitReject}
          onCancel={onCloseRejectModal}
        />
      )}
      {openDetailModal && selectingIdentity && (
        <Modal
          title="Chi tiết căn cước công dân"
          open={openDetailModal}
          onCancel={onCloseDetailModal}
          footer={[
            <Button key="close" onClick={onCloseDetailModal}>
              Đóng
            </Button>,
          ]}
          width={860}
          centered
        >
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Quán ăn" span={2}>
              <Paragraph
                copyable={{
                  text: selectingIdentity.restaurant?._id,
                  tooltips: ["Sao chép ID", "Đã sao chép!"],
                }}
                style={{ margin: 0 }}
              >
                {selectingIdentity.restaurant?.name || "N/A"}
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="Số CCCD">
              {selectingIdentity.number || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Họ và tên">
              {selectingIdentity.name || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Địa chỉ" span={2}>
              {selectingIdentity.address || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày cấp">
              {selectingIdentity.issueDate || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Nơi cấp">
              {selectingIdentity.issuePlace || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày hết hạn">
              {selectingIdentity.expiredDate || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái" span={2}>
              {getStatusTag(selectingIdentity.status)}
            </Descriptions.Item>
            {selectingIdentity.status === "rejected" &&
              selectingIdentity.rejectReason && (
                <Descriptions.Item label="Lý do từ chối" span={2}>
                  {selectingIdentity.rejectReason}
                </Descriptions.Item>
              )}
            {selectingIdentity.processedAt && (
              <Descriptions.Item label="Ngày xử lý" span={2}>
                {dayjs(selectingIdentity.processedAt).format(
                  "DD/MM/YYYY HH:mm"
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Ảnh mặt trước" span={2}>
              {selectingIdentity.frontPhoto ? (
                <Image
                  alt="Front photo"
                  src={getPhotoUrl(selectingIdentity.frontPhoto?.path || null)}
                  width={300}
                  style={{ objectFit: "cover" }}
                />
              ) : (
                "N/A"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Ảnh mặt sau" span={2}>
              {selectingIdentity.backPhoto ? (
                <Image
                  alt="Back photo"
                  src={getPhotoUrl(selectingIdentity.backPhoto?.path || null)}
                  width={300}
                  style={{ objectFit: "cover" }}
                />
              ) : (
                "N/A"
              )}
            </Descriptions.Item>
          </Descriptions>
        </Modal>
      )}
    </>
  );
}

export default RestaurantIdentity;
